import { type MouseEvent } from 'react'
import styled from 'styled-components'
import { PartialEmphasis } from '@/features/shared/components/PartialEmphasis'
import {
  getIsImageType,
  getSuggestionIcon,
  getSuggestionTypeText,
} from '@/features/shared/components/Autocomplete/utils'
import { ArrowTopLeftIcon, NegativeIcon, SearchIcon } from '@shipt/design-system-icons'
import { Body, Headline } from '@shipt/design-system-typography'
import { screenSizes } from '@shipt/design-system-themes'
import { DeleteSuggestions, type Suggestion } from '@/features/shared/services/Autocomplete/types'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { TextButton } from '@shipt/design-system-buttons'
import { trackElementClicked } from '@/features/shared/analytics/element'
import {
  useTrackFilterUtilityClicked,
  trackFilterUtilityViewed,
} from '@/features/shared/analytics/filters'
import { ScrollIntoView } from '@/features/shared/components/ScrollIntoView'
import { DISPLAY_FORMAT } from '@/features/shared/constants/analytics'
import { SUGGESTION_TYPES } from '@/features/shared/constants/global'
import { focusStyles } from '@/features/shared/styles/globalStyles'
import { type OptionProperties } from '@/features/shared/hooks/a11y/useCombobox'
import { hoverShadow } from '@/features/shared/styles/effects'
import { Column } from '@shipt/design-system-layouts'

type Props = {
  userTypedQuery: string
  activeIndex: number | undefined
  suggestions: Suggestion[]
  isModalView: boolean
  onChange: (text: string) => void
  getOptionProperties: (index: number) => OptionProperties
  toggleClearState: () => void
  onDeleteSuggestions: (type: string, suggestions: string[]) => Promise<void>
}

type FilteredSuggestions = {
  standard: Suggestion[]
  personalized: Suggestion[]
  popularImage: Suggestion[]
}

export const SearchSuggestions = ({
  userTypedQuery = '',
  isModalView = false,
  suggestions = [],
  activeIndex,
  onChange,
  getOptionProperties,
  toggleClearState,
  onDeleteSuggestions,
}: Props) => {
  // sorts suggestion types, maintaining original index inside metadata
  const { standard, personalized, popularImage } = suggestions.reduce(
    (acc: FilteredSuggestions, suggestion, index) => {
      const { type, metadata } = suggestion
      const {
        PERSONALIZED_RECENT,
        PERSONALIZED_RECENT_IMAGE,
        PERSONALIZED_POPULAR,
        PERSONALIZED_POPULAR_IMAGE,
        IMAGE_SUGGESTION,
      } = SUGGESTION_TYPES
      const newSuggestion = {
        ...suggestion,
        metadata: {
          ...metadata,
          index,
        },
      }
      if (
        type === PERSONALIZED_RECENT ||
        type === PERSONALIZED_RECENT_IMAGE ||
        type === PERSONALIZED_POPULAR ||
        type === PERSONALIZED_POPULAR_IMAGE
      ) {
        acc.personalized.push(newSuggestion)
      } else if (type === IMAGE_SUGGESTION) {
        acc.popularImage.push(newSuggestion)
      } else {
        acc.standard.push(newSuggestion)
      }
      return acc
    },
    { standard: [], personalized: [], popularImage: [] },
  )

  const trackFilterUtilityClicked = useTrackFilterUtilityClicked()

  const handleDeleteSuggestion = (type: string, term: string) => {
    const deletionType =
      type === SUGGESTION_TYPES.PERSONALIZED_RECENT ||
      type === SUGGESTION_TYPES.PERSONALIZED_RECENT_IMAGE
        ? DeleteSuggestions.Recent
        : DeleteSuggestions.Popular
    onDeleteSuggestions(deletionType, [term])
  }

  const handleClearAllButtonClick = (e: MouseEvent) => {
    trackElementClicked({
      link_name: 'universal_search_bar_clear',
    })
    e.preventDefault()
    toggleClearState()
  }

  const getImageSrc = (metadata: Suggestion['metadata']): string => {
    if (metadata.image_base64) {
      return `data:image/jpeg;base64,${metadata.image_base64}`
    }
    return metadata.image_url ?? ''
  }
  const hasClearAllButton = personalized.length > 4

  return (
    <SuggestionContainer>
      {personalized.length > 0 && personalized[0] !== undefined && (
        <>
          {!userTypedQuery && (
            <SuggestionTitle title={getSuggestionTypeText(personalized[0].type)} />
          )}
          {personalized.map((suggestion) => {
            const { term, type, metadata } = suggestion
            const hasImage = getIsImageType(type)

            return (
              <SuggestionList key={term} {...getOptionProperties(metadata.index)}>
                <SuggestionWrapper
                  data-is-active={activeIndex === metadata.index}
                  hasImage={hasImage}
                >
                  {hasImage ? (
                    <SuggestionImage
                      src={getImageSrc(metadata)}
                      width={40}
                      height={40}
                      // leave this empty - the suggestion term describes the image
                      alt=""
                      style={{ objectFit: 'contain' }}
                      fallbackType="suggestion"
                    />
                  ) : (
                    <SuggestionTermIcon as={getSuggestionIcon(type)} />
                  )}
                  <PartialEmphasisWrapper>
                    <PartialEmphasis text={term} emphasis={userTypedQuery} />
                  </PartialEmphasisWrapper>
                  <DeleteSuggestionIcon
                    onClick={(e: MouseEvent) => {
                      e.stopPropagation()
                      handleDeleteSuggestion(type, term)
                    }}
                    tabIndex={-1}
                    aria-label={`Select to delete this personalized suggestion ${term}`}
                  />
                </SuggestionWrapper>
              </SuggestionList>
            )
          })}
        </>
      )}
      {!!hasClearAllButton && (
        <ClearButtonContainer>
          <TextButton onClick={handleClearAllButtonClick}>Clear all recent searches</TextButton>
        </ClearButtonContainer>
      )}
      <Column marginTop={!hasClearAllButton ? 'xl' : undefined}>
        {!userTypedQuery && standard.length > 0 && <SuggestionTitle title="Popular" />}
        {standard.map((suggestion) => {
          const { term, type, metadata } = suggestion
          const hasImage = getIsImageType(type)

          return (
            <SuggestionList key={term} {...getOptionProperties(metadata.index)}>
              <SuggestionWrapper
                data-is-active={activeIndex === metadata.index}
                hasImage={hasImage}
              >
                {hasImage ? (
                  <SuggestionImage
                    src={getImageSrc(metadata)}
                    width={40}
                    height={40}
                    // leave this empty - the suggestion term describes the image
                    alt=""
                    style={{ objectFit: 'contain' }}
                    fallbackType="suggestion"
                  />
                ) : (
                  <SuggestionTermIcon as={getSuggestionIcon(type)} />
                )}
                <PartialEmphasisWrapper>
                  <PartialEmphasis text={term} emphasis={userTypedQuery} />
                </PartialEmphasisWrapper>
                {!!isModalView && (
                  <SuggestionTypeAheadIcon
                    inherit
                    as={hasImage ? SearchIcon : ArrowTopLeftIcon}
                    onClick={(e: MouseEvent) => {
                      trackElementClicked({
                        link_name: 'universal_search_autocomplete_clicked',
                      })
                      e.stopPropagation()
                      onChange(term)
                    }}
                  />
                )}
              </SuggestionWrapper>
            </SuggestionList>
          )
        })}
      </Column>
      {popularImage.length > 0 && (
        <>
          <SuggestionList>
            <SuggestionTitle title="Popular" />
          </SuggestionList>
          <PopularImagesContainer>
            {popularImage.map((suggestion, idx) => {
              const { term, metadata } = suggestion
              return (
                <PopularImageCard
                  key={term}
                  {...getOptionProperties(metadata.index)}
                  onClick={() => {
                    getOptionProperties(metadata.index).onClick()
                    trackFilterUtilityClicked({
                      filter_value: term,
                      utility_type: 'popular_cards',
                      display_format: DISPLAY_FORMAT.GRID,
                      filter_active: false,
                      grid_index: idx,
                    })
                  }}
                >
                  <PopularImageScrollIntoView
                    onInView={() => {
                      trackFilterUtilityViewed({
                        list_index: idx,
                        utility_type: 'popular_cards',
                        filter_value: term,
                        display_format: DISPLAY_FORMAT.GRID,
                      })
                    }}
                  >
                    <PopularImageContainer>
                      <Image
                        src={metadata.image_url ?? ''}
                        fallbackType="product"
                        // leave this empty - the suggestion term describes the image
                        alt=""
                        width={80}
                        height={80}
                      />
                    </PopularImageContainer>
                    <PopularImageLabel size="md" align="center">
                      {term}
                    </PopularImageLabel>
                  </PopularImageScrollIntoView>
                </PopularImageCard>
              )
            })}
          </PopularImagesContainer>
        </>
      )}
    </SuggestionContainer>
  )
}

const SuggestionTitle = ({ title }: { title: string }) => (
  <SuggestionTypeWrapper onClick={(e) => e.stopPropagation()}>
    <Headline variant="primary" concept="base" size="sm">
      {title}
    </Headline>
  </SuggestionTypeWrapper>
)

const PopularImageScrollIntoView = styled(ScrollIntoView)`
  height: auto;
  width: auto;
  max-width: 100px;
  display: grid;
  place-items: center;
`

const SuggestionContainer = styled.div`
  max-height: calc(100vh - 13.75rem);
  overflow-y: auto;
`

const SuggestionList = styled.li`
  list-style-type: none;
`

const SuggestionImage = styled(Image)`
  margin-right: 16px;
`

const SuggestionTermIcon = styled.svg`
  height: 1rem;
  width: 1rem;
  margin-right: 16px;
  color: ${({ theme }) => theme.gray600};
  min-width: 1rem;
`

const SuggestionTypeAheadIcon = styled(ArrowTopLeftIcon)`
  padding: 0.5rem;
  color: ${({ theme }) => theme.gray600};
`

const PartialEmphasisWrapper = styled.div`
  width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const SuggestionTypeWrapper = styled.div`
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
`

const SuggestionWrapper = styled.div<{
  'data-is-active': boolean
  hasImage: boolean
}>`
  cursor: pointer;
  display: flex;
  align-items: center;
  height: 2.5rem;
  padding: 0 1rem 0 2rem;
  margin-top: ${({ hasImage }) => (hasImage ? '0.5rem' : '0')};

  @media ${screenSizes.tablet} {
    height: ${({ hasImage }) => (hasImage ? '2.5rem' : '2rem')};
    padding: 0 1rem;

    &[data-is-active='true'],
    &:hover,
    &:focus,
    &:active {
      padding-left: 0.875rem;
      border-left: 0.125rem solid ${({ theme }) => theme.primary};
      background-color: ${({ theme }) => theme.plum50};
    }
  }
`

const DeleteSuggestionIcon = styled(NegativeIcon)`
  width: 1.25rem;
  height: 1rem;
  padding: 0.25rem;
  border-radius: 100%;
  color: ${({ theme }) => theme.gray600};

  &:hover,
  &:focus {
    background-color: ${({ theme }) => theme.plum100};
  }
`

const ClearButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 0.375rem 0;
`

const PopularImagesContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(96px, 1fr));
  width: 100%;
  height: auto;
  padding: 0.75rem 2.5rem;
  gap: 1.5rem;

  @media ${screenSizes.max_mobile} {
    gap: 1.5rem 0.5rem;
  }
`
const PopularImageCard = styled.li`
  list-style-type: none;
  max-width: 100%;

  &[aria-selected='true'] {
    ${focusStyles}
  }
`
const PopularImageContainer = styled.div`
  border: 1px solid ${({ theme }) => theme.gray300};
  border-radius: 0.5rem;
  padding: 0.5rem;
  display: flex;
  width: 6rem;
  height: 6rem;

  ${hoverShadow}

  &:hover {
    cursor: pointer;
    border: none;
  }
`

const PopularImageLabel = styled(Body)`
  width: 6rem;
  margin-top: 0.5rem;
`
